import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { ref, uploadString, getDownloadURL } from "@firebase/storage";
import { storage } from "../../main-component/Firebase";
import "./AdminDesign.css";

const DEFAULT_DESIGN = {
  heroImage: "",
  heroButtonLink: "/evenimente/mostre",
  heroButtonText: "Explorează Colecția",
  footerLinks: [
    { label: "Acasă", path: "/" },
    { label: "Despre noi", path: "/despre-noi" },
    { label: "Misiune", path: "/misiune" },
    { label: "Evenimente", path: "/evenimente" },
    { label: "Produse", path: "/evenimente/mostre" },
    { label: "Contact", path: "/contact" },
  ],
};

const AdminDesign = () => {
  const [design, setDesign] = useState(DEFAULT_DESIGN);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [heroPreview, setHeroPreview] = useState("");
  const [base64URL, setBase64URL] = useState("");
  const [activeTab, setActiveTab] = useState("hero");
  const inputRef = useRef(null);

  useEffect(() => {
    fetchDesign();
  }, []);

  const fetchDesign = async () => {
    try {
      // Load heroImage from the unified siteDesign doc (same as AdminImageZoom)
      const imageRes = await axios.get(
        `${process.env.REACT_APP_API_BASE}/imageSettings/get`
      );
      const imageData = imageRes.data || {};
      if (imageData.heroImage) setHeroPreview(imageData.heroImage);

      // Load text settings (heroButtonText, heroButtonLink, footerLinks)
      const textRes = await axios.get(
        `${process.env.REACT_APP_API_BASE}/settings/get?category=siteDesign`
      );
      const data = Array.isArray(textRes.data) ? textRes.data : [];
      if (data.length > 0) {
        const saved = data[0];
        setDesign({
          heroImage: imageData.heroImage || saved.heroImage || "",
          heroButtonLink: saved.heroButtonLink || "/evenimente/mostre",
          heroButtonText: saved.heroButtonText || "Explorează Colecția",
          footerLinks: saved.footerLinks || DEFAULT_DESIGN.footerLinks,
          docId: saved.docId,
        });
        if (!imageData.heroImage && saved.heroImage) setHeroPreview(saved.heroImage);
      } else {
        setDesign((prev) => ({ ...prev, heroImage: imageData.heroImage || "" }));
      }
    } catch (err) {
      console.error("Error fetching design settings:", err);
    } finally {
      setLoading(false);
    }
  };

  const getBase64 = (file) =>
    new Promise((resolve) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
    });

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    getBase64(file).then((result) => {
      setBase64URL(result);
      setHeroPreview(result);
    });
  };

  const handleFooterLinkChange = (index, field, value) => {
    setDesign((prev) => {
      const links = [...prev.footerLinks];
      links[index] = { ...links[index], [field]: value };
      return { ...prev, footerLinks: links };
    });
  };

  const addFooterLink = () => {
    setDesign((prev) => ({
      ...prev,
      footerLinks: [...prev.footerLinks, { label: "", path: "/" }],
    }));
  };

  const removeFooterLink = (index) => {
    setDesign((prev) => ({
      ...prev,
      footerLinks: prev.footerLinks.filter((_, i) => i !== index),
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      let heroImageUrl = design.heroImage;

      if (base64URL) {
        // Use the SAME Firebase Storage path as AdminImageZoom so the hero
        // image change is visible to all visitors immediately.
        const imageRef = ref(storage, "siteDesign/heroImage");
        await uploadString(imageRef, base64URL, "data_url");
        heroImageUrl = await getDownloadURL(imageRef);

        // Persist to the siteDesign Firestore doc (read by AdminImageZoom & pre-fetch)
        await axios.put(
          `${process.env.REACT_APP_API_BASE}/imageSettings/save`,
          { heroImage: heroImageUrl },
          { headers: { Authorization: `Basic ${process.env.REACT_APP_TOKEN}` } }
        );
      }

      // Persist text settings to the settings collection
      const textPayload = {
        category: "siteDesign",
        heroButtonLink: design.heroButtonLink,
        heroButtonText: design.heroButtonText,
        footerLinks: design.footerLinks,
      };

      if (design.docId) {
        await axios.put(
          `${process.env.REACT_APP_API_BASE}/settings/update?id=${design.docId}`,
          textPayload,
          { headers: { Authorization: `Basic ${process.env.REACT_APP_TOKEN}` } }
        );
      } else {
        await axios.post(
          `${process.env.REACT_APP_API_BASE}/settings/post`,
          { ...textPayload, id: Date.now() },
          { headers: { Authorization: `Basic ${process.env.REACT_APP_TOKEN}` } }
        );
      }

      toast.success("Design salvat cu succes!");
      setBase64URL("");
      fetchDesign();
    } catch (err) {
      console.error("Error saving design:", err);
      toast.error("Eroare la salvarea setărilor de design");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-design__loading">
        <div className="admin-design__spinner"></div>
        <span>Se încarcă setările...</span>
      </div>
    );
  }

  return (
    <div className="admin-design">
      {/* Tab Navigation */}
      <div className="admin-design__tabs">
        <button
          className={`admin-design__tab ${activeTab === "hero" ? "admin-design__tab--active" : ""}`}
          onClick={() => setActiveTab("hero")}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
            <line x1="8" y1="21" x2="16" y2="21" />
            <line x1="12" y1="17" x2="12" y2="21" />
          </svg>
          Hero / Slider
        </button>
        <button
          className={`admin-design__tab ${activeTab === "footer" ? "admin-design__tab--active" : ""}`}
          onClick={() => setActiveTab("footer")}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
          </svg>
          Footer — Navigare
        </button>
      </div>

      {/* Hero Section Tab */}
      {activeTab === "hero" && (
        <div className="admin-design__panel">
          <div className="admin-design__panel-header">
            <h4 className="admin-design__panel-title">Slider / Hero</h4>
            <p className="admin-design__panel-desc">Configurează imaginea de fundal și butonul principal de pe homepage.</p>
          </div>

          {/* Image Upload Card */}
          <div className="admin-design__card">
            <div className="admin-design__card-label">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <polyline points="21 15 16 10 5 21" />
              </svg>
              Imagine de fundal
            </div>
            {heroPreview ? (
              <div className="admin-design__image-preview">
                <img src={heroPreview} alt="Hero preview" />
                <div className="admin-design__image-overlay">
                  <button
                    className="admin-design__image-change"
                    onClick={() => inputRef.current && inputRef.current.click()}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                    </svg>
                    Schimbă imaginea
                  </button>
                </div>
              </div>
            ) : (
              <div
                className="admin-design__image-drop"
                onClick={() => inputRef.current && inputRef.current.click()}
              >
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
                <span>Click pentru a încărca imaginea hero</span>
                <span className="admin-design__image-hint">PNG, JPG sau WebP • Recomandat: 1920×1080</span>
              </div>
            )}
            <input
              type="file"
              accept="image/*"
              ref={inputRef}
              onChange={handleImageUpload}
              style={{ display: "none" }}
            />
          </div>

          {/* Button Settings */}
          <div className="admin-design__card">
            <div className="admin-design__card-label">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
                <polyline points="10 17 15 12 10 7" />
                <line x1="15" y1="12" x2="3" y2="12" />
              </svg>
              Butonul principal
            </div>
            <div className="admin-design__inline-fields">
              <div className="admin-design__field">
                <label>Text buton</label>
                <input
                  type="text"
                  value={design.heroButtonText}
                  onChange={(e) => setDesign((prev) => ({ ...prev, heroButtonText: e.target.value }))}
                  placeholder="Explorează Colecția"
                />
              </div>
              <div className="admin-design__field">
                <label>Link (cale)</label>
                <input
                  type="text"
                  value={design.heroButtonLink}
                  onChange={(e) => setDesign((prev) => ({ ...prev, heroButtonLink: e.target.value }))}
                  placeholder="/evenimente/mostre"
                />
              </div>
            </div>
            {/* Live Preview */}
            <div className="admin-design__btn-preview">
              <span className="admin-design__btn-preview-label">Previzualizare:</span>
              <span className="admin-design__btn-mock">{design.heroButtonText || "Buton"}</span>
            </div>
          </div>
        </div>
      )}

      {/* Footer Tab */}
      {activeTab === "footer" && (
        <div className="admin-design__panel">
          <div className="admin-design__panel-header">
            <h4 className="admin-design__panel-title">Footer — Navigare</h4>
            <p className="admin-design__panel-desc">Link-urile care apar în footer sub secțiunea „Navigare". Ordinea se reflectă pe site.</p>
          </div>

          <div className="admin-design__links-list">
            {design.footerLinks.map((link, i) => (
              <div key={i} className="admin-design__link-item">
                <div className="admin-design__link-number">{i + 1}</div>
                <div className="admin-design__link-fields">
                  <input
                    type="text"
                    value={link.label}
                    onChange={(e) => handleFooterLinkChange(i, "label", e.target.value)}
                    placeholder="Etichetă"
                  />
                  <input
                    type="text"
                    value={link.path}
                    onChange={(e) => handleFooterLinkChange(i, "path", e.target.value)}
                    placeholder="/pagina"
                  />
                </div>
                <button
                  className="admin-design__link-remove"
                  onClick={() => removeFooterLink(i)}
                  title="Șterge"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M18 6L6 18M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ))}
          </div>

          <button className="admin-design__link-add" onClick={addFooterLink}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Adaugă link
          </button>
        </div>
      )}

      {/* Save Button */}
      <div className="admin-design__actions">
        <button
          className="admin-design__save"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? (
            <>
              <div className="admin-design__save-spinner"></div>
              Se salvează...
            </>
          ) : (
            <>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              Salvează Design
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default AdminDesign;
